import { useLayoutEffect, useRef, useState, type DependencyList } from "react";

/**
 * Tracks inline "|" separators in a flex row that wraps, so a separator never dangles at the end of
 * a line. Mark each separator element with the `BasicInfo--separator` class; the returned boolean
 * array is indexed by separator order (0 = first separator) and is `true` when the item following
 * that separator has wrapped to a new line.
 *
 * Re-measures on container resize (via ResizeObserver) and whenever `deps` change (content swaps
 * that add or remove children, which a ResizeObserver alone may not catch).
 */
const SEPARATOR_CLASS = "BasicInfo--separator";

export const useWrappedSeparators = <T extends HTMLElement>(deps: DependencyList) => {
  const rowRef = useRef<T>(null);
  const [wrappedSeparators, setWrappedSeparators] = useState<boolean[]>([]);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const measure = () => {
      const children = Array.from(row.children) as HTMLElement[];
      const isSeparator = (el: HTMLElement) => el.classList.contains(SEPARATOR_CLASS);
      const result: boolean[] = [];

      children.forEach((el, i) => {
        if (!isSeparator(el)) return;
        const prev = children
          .slice(0, i)
          .reverse()
          .find((c) => !isSeparator(c));
        const next = children.slice(i + 1).find((c) => !isSeparator(c));
        if (!prev || !next) {
          result.push(false);
          return;
        }
        // The following item wrapped when it starts at or below the previous item's bottom edge.
        result.push(next.getBoundingClientRect().top >= prev.getBoundingClientRect().bottom);
      });

      setWrappedSeparators(result);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { rowRef, wrappedSeparators };
};

export default useWrappedSeparators;
